// src/features/services/state/servicesSlice.js
//
// Redux Toolkit slice for Host Services
// - Normalized state by service key
// - Async thunks for catalog load, host config load, single + bulk updates, provider quote
// - Optimistic updates with per-service rollback on failure
// - Selectors for easy UI consumption
//

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';

import {
  fetchServicesCatalog,
  fetchHostServices,
  saveHostServicesAPI,     // bulk updates
  updateHostServiceAPI,    // single update
  createServiceQuote,      // provider quote
  fetchCatalogAndHost,     // combined endpoint
  createCustomServiceAPI,  // host-created custom service
} from '../../services/servicesApi'; // <-- adjust if your path differs

// ──────────────────────────────────────────────────────────────
// Thunks
// ──────────────────────────────────────────────────────────────

// Catalog only
export const fetchCatalog = createAsyncThunk(
  'services/fetchCatalog',
  async () => {
    const list = await fetchServicesCatalog();
    return Array.isArray(list) ? list : (list?.services || []);
  }
);

// Host services (map only)
export const fetchHostServicesThunk = createAsyncThunk(
  'services/fetchHostServices',
  async (hostId, { rejectWithValue }) => {
    try {
      const servicesMap = await fetchHostServices(hostId);
      return { hostId, servicesMap };
    } catch (e) {
      return rejectWithValue(e?.message || 'Failed to load host services');
    }
  }
);

// One-shot load (catalog + host map) with fallback
export const loadCatalogAndHost = createAsyncThunk(
  'services/loadCatalogAndHost',
  async (hostId, { dispatch, rejectWithValue }) => {
    try {
      // Try combined endpoint first
      const { catalog = [], servicesMap = {} } = await fetchCatalogAndHost(hostId);
      return { hostId, catalog, servicesMap };
    } catch (e) {
      // Fallback: still fetch catalog so UI can render
      try {
        const [catalogArr, hostRes] = await Promise.allSettled([
          dispatch(fetchCatalog()),
          dispatch(fetchHostServicesThunk(hostId)),
        ]);

        const catalog =
          catalogArr.status === 'fulfilled' ? catalogArr.value : [];

        const servicesMap =
          hostRes.status === 'fulfilled'
            ? (hostRes.value?.servicesMap || {})
            : {};

        // Even if host map failed, we still return catalog so UI shows
        return { hostId, catalog, servicesMap };
      } catch (fallbackErr) {
        return rejectWithValue(
          e?.message || fallbackErr?.message || 'Failed to load services'
        );
      }
    }
  }
);

// Single update (optimistic)
export const updateService = createAsyncThunk(
  'services/updateService',
  async ({ hostId, key, patch }, { rejectWithValue }) => {
    try {
      const resp = await updateHostServiceAPI(hostId, key, patch);
      return { key, service: resp.service };
    } catch (e) {
      return rejectWithValue({ key, message: e?.message || 'Update failed' });
    }
  }
);

// Bulk update (optimistic)
export const bulkUpdateServices = createAsyncThunk(
  'services/bulkUpdate',
  async ({ hostId, updates }, { rejectWithValue }) => {
    try {
      const resp = await saveHostServicesAPI(hostId, updates);
      return { servicesMap: resp.servicesMap || {} };
    } catch (e) {
      return rejectWithValue(e?.message || 'Bulk update failed');
    }
  }
);

// Provider quote request (optional)
export const requestQuote = createAsyncThunk(
  'services/requestQuote',
  async ({ serviceKey, params }, { rejectWithValue }) => {
    try {
      const quote = await createServiceQuote(serviceKey, params);
      return { serviceKey, quote };
    } catch (e) {
      return rejectWithValue(e?.message || 'Quote failed');
    }
  }
);

// Create custom host service
export const createCustomService = createAsyncThunk(
  'services/createCustomService',
  async ({ hostId, serviceData }, { rejectWithValue }) => {
    try {
      const resp = await createCustomServiceAPI(hostId, serviceData);
      return { key: resp.service.key, service: resp.service };
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to create service');
    }
  }
);

// ──────────────────────────────────────────────────────────────
// Initial State
// ──────────────────────────────────────────────────────────────

const initialState = {
  catalog: {
    byKey: {},
    allKeys: [],
    status: 'idle',
    error: null,
  },
  host: {
    hostId: null,
    services: {}, // { [key]: { key, enabled, visibility, pricing, propertyIds? } }
    status: 'idle',
    error: null,
  },
  saveStatus: {},     // key -> 'idle'|'saving'|'saved'|'error'
  bulkStatus: 'idle',
  lastQuote: null,    // { serviceKey, quote }
  quoteStatus: 'idle',
  error: null,
  _optimistic: {},    // key -> previous snapshot
};

// ──────────────────────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────────────────────

const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    clearQuote(state) {
      state.lastQuote = null;
      state.quoteStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    // ── Catalog
    builder
      .addCase(fetchCatalog.pending, (state) => {
        state.catalog.status = 'loading';
        state.catalog.error = null;
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        state.catalog.status = 'succeeded';
        const byKey = {};
        const allKeys = [];
        for (const item of action.payload || []) {
          if (!item?.key) continue;
          byKey[item.key] = item;
          allKeys.push(item.key);
        }
        state.catalog.byKey = byKey;
        state.catalog.allKeys = allKeys;
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        state.catalog.status = 'failed';
        state.catalog.error = action.payload || action.error?.message || 'Failed to load catalog';
      });

    // ── Host services
    builder
      .addCase(fetchHostServicesThunk.pending, (state) => {
        state.host.status = 'loading';
        state.host.error = null;
      })
      .addCase(fetchHostServicesThunk.fulfilled, (state, action) => {
        state.host.status = 'succeeded';
        const { hostId, servicesMap } = action.payload || {};
        state.host.hostId = hostId || state.host.hostId;
        state.host.services = servicesMap || {};
      })
      .addCase(fetchHostServicesThunk.rejected, (state, action) => {
        state.host.status = 'failed';
        state.host.error = action.payload || action.error?.message || 'Failed to load host services';
      });

    // ── One-shot load (catalog + host map) with fallback
    builder
      .addCase(loadCatalogAndHost.pending, (state) => {
        state.catalog.status = 'loading';
        state.host.status = 'loading';
        state.catalog.error = null;
        state.host.error = null;
      })
      .addCase(loadCatalogAndHost.fulfilled, (state, action) => {
        const { hostId, catalog = [], servicesMap = {} } = action.payload || {};

        // catalog
        state.catalog.status = 'succeeded';
        const byKey = {};
        const allKeys = [];
        for (const s of catalog) {
          if (!s?.key) continue;
          byKey[s.key] = s;
          allKeys.push(s.key);
        }
        state.catalog.byKey = byKey;
        state.catalog.allKeys = allKeys;

        // host map
        state.host.status = 'succeeded';
        state.host.hostId = hostId || state.host.hostId;
        state.host.services = servicesMap || {};
      })
      .addCase(loadCatalogAndHost.rejected, (state, action) => {
        state.catalog.status = 'failed';
        state.host.status = 'failed';
        const msg = action.payload || action.error?.message || 'Failed to load services';
        state.catalog.error = msg;
        state.host.error = msg;
      });

    // ── Single update (optimistic)
    builder
      .addCase(updateService.pending, (state, action) => {
        const { hostId, key, patch } = action.meta.arg || {};
        if (!key) return;

        const prev = state.host.services[key] || null;
        state._optimistic[key] = prev ? { ...prev } : null;

        const next = {
          ...(prev || { key, enabled: false, visibility: 'public', pricing: {}, propertyIds: [] }),
          ...patch,
        };
        if (patch?.pricing) next.pricing = { ...(prev?.pricing || {}), ...patch.pricing };
        if (Array.isArray(patch?.propertyIds)) next.propertyIds = [...patch.propertyIds];

        state.host.hostId = hostId || state.host.hostId;
        state.host.services[key] = next;
        state.saveStatus[key] = 'saving';
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const { key, service } = action.payload || {};
        if (key && service) {
          state.host.services[key] = service; // authoritative from server
          delete state._optimistic[key];
          state.saveStatus[key] = 'saved';
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        const { key } = action.payload || action.meta.arg || {};
        const prev = key ? state._optimistic[key] : null;
        if (key) {
          if (prev === null) delete state.host.services[key];
          else state.host.services[key] = prev;
          delete state._optimistic[key];
          state.saveStatus[key] = 'error';
        }
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : (action.payload?.message || action.error?.message || 'Update failed');
      });

    // ── Bulk update (optimistic)
    builder
      .addCase(bulkUpdateServices.pending, (state, action) => {
        state.bulkStatus = 'saving';
        const updates = action.meta.arg?.updates || [];
        updates.forEach((u) => {
          if (!u?.key) return;
          const prev = state.host.services[u.key] || null;
          state._optimistic[u.key] = prev ? { ...prev } : null;

          const next = {
            ...(prev || { key: u.key, enabled: false, visibility: 'public', pricing: {}, propertyIds: [] }),
            ...u,
          };
          if (u.pricing) next.pricing = { ...(prev?.pricing || {}), ...u.pricing };
          if (Array.isArray(u.propertyIds)) next.propertyIds = [...u.propertyIds];

          state.host.services[u.key] = next;
          state.saveStatus[u.key] = 'saving';
        });
      })
      .addCase(bulkUpdateServices.fulfilled, (state, action) => {
        const { servicesMap } = action.payload || {};
        if (servicesMap && typeof servicesMap === 'object') {
          state.host.services = servicesMap; // authoritative replace
          Object.keys(state._optimistic).forEach((k) => (state.saveStatus[k] = 'saved'));
          state._optimistic = {};
        } else {
          Object.keys(state._optimistic).forEach((k) => {
            state.saveStatus[k] = 'saved';
            delete state._optimistic[k];
          });
        }
        state.bulkStatus = 'idle';
      })
      .addCase(bulkUpdateServices.rejected, (state, action) => {
        Object.entries(state._optimistic).forEach(([k, prev]) => {
          if (prev === null) delete state.host.services[k];
          else state.host.services[k] = prev;
          state.saveStatus[k] = 'error';
        });
        state._optimistic = {};
        state.bulkStatus = 'idle';
        state.error = action.payload || action.error?.message || 'Bulk update failed';
      });

    // ── Quotes
    builder
      .addCase(requestQuote.pending, (state) => {
        state.quoteStatus = 'loading';
        state.lastQuote = null;
      })
      .addCase(requestQuote.fulfilled, (state, action) => {
        state.quoteStatus = 'succeeded';
        state.lastQuote = action.payload; // { serviceKey, quote }
      })
      .addCase(requestQuote.rejected, (state, action) => {
        state.quoteStatus = 'failed';
        state.lastQuote = null;
        state.error = action.payload || action.error?.message || 'Quote failed';
      })

    // ── Create custom service
    builder
      .addCase(createCustomService.fulfilled, (state, action) => {
        const { key, service } = action.payload || {};
        if (key && service) {
          state.host.services[key] = service;
          state.saveStatus[key] = 'saved';
        }
      })
      .addCase(createCustomService.rejected, (state, action) => {
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : (action.error?.message || 'Create custom service failed');
      });
  },
});

// ──────────────────────────────────────────────────────────────
// Selectors
// ──────────────────────────────────────────────────────────────

const base = (state) => state.services;

export const selectCatalogStatus = (state) => base(state).catalog.status;
export const selectHostStatus    = (state) => base(state).host.status;

export const selectCatalogList = createSelector(
  (state) => base(state).catalog.allKeys,
  (state) => base(state).catalog.byKey,
  (all, byKey) => all.map((k) => byKey[k])
);

export const selectCatalogByKey = (key) =>
  createSelector(
    (state) => base(state).catalog.byKey,
    (byKey) => byKey[key]
  );

export const selectHostId      = (state) => base(state).host.hostId;
export const selectServicesMap = (state) => base(state).host.services;

export const selectServiceByKey = (key) =>
  createSelector(selectServicesMap, (map) => map[key]);

export const selectServiceSaveStatus = (key) =>
  createSelector(
    (state) => base(state).saveStatus,
    (saveStatus) => saveStatus[key] || 'idle'
  );

export const selectServicesGrouped = createSelector(
  selectCatalogList,
  selectServicesMap,
  (catalog, map) => {
    const groups = {};
    (catalog || []).forEach((def) => {
      if (!def) return;
      const cat = def.category || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ def, host: map[def.key] || null });
    });
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => (a.def.name || '').localeCompare(b.def.name || ''))
    );
    return groups;
  }
);

export const selectQuote       = (state) => base(state).lastQuote;
export const selectQuoteStatus = (state) => base(state).quoteStatus;

export const { clearQuote } = servicesSlice.actions;
export default servicesSlice.reducer;
